import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCurrentHoldingVT2 } from 'views/Portfolio/components/Details/services/fetchCurrentHoldings';
import VerifyTopUpAndContinueToPurchase from 'views/shared/SlideOutViews/VerifyTopUpAndContinueToPurchase ';
import { useLocation } from 'react-router-dom';
import { Tabs } from '../../../../components';
import { TabState } from '../../../../components/Layout/SortAndFilterLayout';
import { TabType } from '../../../../components/Tabs';
import { DATA_REFS, Product } from '../../../../types/productType';
import { capitalizeFirstLetter, sortItems, sumBy, toInternalId, uniqueItems } from '../../../../utils';
import { DEFAULT_LATEST_DEAL_DATE, initialiseCurrentTabVT2, initialiseSoldTab, PortfolioViewMode } from '../../helpers';
import { SortByOption } from '../../types';
import StockTab from '../../../../components/StockTab';
import { FilterTypes } from '../../../../components/Filters';
import { AppContext } from '../../../../context/ContextProvider';
import { fetchSoldHoldings } from './services/fetchSoldHoldings';
import { fetchExternalHoldings } from './services/fetchExternalHoldings';
import { usePortalHoldings, PortalHolding } from '../../../MyCellar/hooks/usePortalHoldings';
import TradeDistribution from './TradeDistribution';
import PortfolioViewToggle from './PortfolioViewToggle';

enum TabTypes {
  CURRENT = 'current',
  SOLD = 'sold',
  EXTERNAL = 'external',
}

const defaultSortKey = DATA_REFS.NAME;

const HOLDING_TIME_OUT = 1500;

export const Details = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    formatter: currencyFormatter,
    state: {
      settings: { currency, selectedPortfolioId },
    },
    gbpToTargetCurrency,
  } = useContext(AppContext);

  const formatter = useMemo(
    () => ({ format: (value: number) => currencyFormatter.format(value, true) }),
    [currencyFormatter],
  );

  // Fetch market insights with review data
  const marketInsights = usePortalHoldings(selectedPortfolioId);

  const [tabStates, setTabStates] = useState({
    [TabTypes.CURRENT]: null,
    [TabTypes.SOLD]: null,
    [TabTypes.EXTERNAL]: null,
  });
  const currentTabState = useRef<TabState | null>(null);
  const [selectedTab, setSelectedTab] = useState(TabTypes.CURRENT);
  const [searchQuery] = useState('');
  const [currentViewMode, setCurrentViewMode] = useState<PortfolioViewMode>('case');
  const {
    columns,
    sortByOptions,
    defaultSortBy: currentDefaultSortBy,
  } = useMemo(() => {
    return initialiseCurrentTabVT2(t, formatter, currentViewMode);
  }, [t, formatter, currentViewMode]);

  const {
    columns: soldStockColumns,
    sortByOptions: soldSortByOptions,
    defaultSortBy: soldDefaultSortBy,
  } = useMemo(() => initialiseSoldTab(t, formatter), [t, formatter]);

  const onStateChange = (state: TabState | null) => {
    currentTabState.current = state;
  };

  const onTabSelect = (tab: TabType) => {
    setSelectedTab(tab.value as TabTypes);
    setTabStates({ ...tabStates, [currentTabState.current?.id as TabTypes]: currentTabState.current });
  };

  // portal/CurrentHoldings is not paginated - it returns every holding line in a
  // single response, already grouped by lwin18 (a wine held across several
  // sub-portfolios comes back as one line).
  const {
    data: currentHoldingsVT2,
    isLoading: loadingCurrent,
    error: currentError,
  } = useQuery({
    queryFn: () => fetchCurrentHoldingVT2(selectedPortfolioId ? { portfolioId: selectedPortfolioId } : undefined),
    queryKey: ['fetchCurrentHoldingVT2', { currency, selectedPortfolioId }],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    keepPreviousData: true, // Show stale data while fetching new data
  });

  const currentHoldings = useMemo(() => {
    const allItems = currentHoldingsVT2?.data ?? [];
    if (allItems.length === 0) return [];
    const sortConfig = {
      sortBy: location?.state?.defaultSort ?? defaultSortKey,
      isAsc: location?.state?.isAsc !== undefined ? location?.state?.isAsc : true,
    };
    // Guard the headline valuation: a holding must never be summed twice, even
    // if the source ever returns the same wine on more than one row.
    return sortItems(uniqueItems(allItems, 'lwin18'), sortConfig.isAsc, sortConfig.sortBy);
  }, [currentHoldingsVT2?.data, location?.state?.defaultSort, location?.state?.isAsc]);

  // Denominator for the "x of y results" counter. This has to be the number of
  // holding lines in the grid - the API's `size` counts asset units, so a wine
  // held as 5 cases counts 5 times and the two numbers never reconcile.
  const totalHoldingsCount = useMemo(
    () => (currentHoldings.length > 0 ? currentHoldings.length : undefined),
    [currentHoldings],
  );

  // Lazy load sold stocks - only fetch when tab is selected
  const {
    data: soldStocksVT2,
    isFetching: loadingSoldStocks,
    error: soldStockError,
  } = useQuery({
    queryFn: () => fetchSoldHoldings(selectedPortfolioId ? { portfolioId: selectedPortfolioId } : {}),
    queryKey: ['fetchSoldHoldings', { currency, selectedPortfolioId }],
    refetchOnWindowFocus: false,
    enabled: selectedTab === TabTypes.SOLD, // Only fetch when sold tab is selected
    staleTime: 5 * 60 * 1000, // Keep data fresh for 5 minutes
  });

  const soldStocks = useMemo(() => {
    if (loadingSoldStocks || !soldStocksVT2?.data) return [];
    return sortItems([...(soldStocksVT2?.data ?? [])], false, DATA_REFS.SOLD_DATE);
  }, [soldStocksVT2?.data, loadingSoldStocks]);

  // Memoize the enrichment function to avoid recreating on every render
  const enrichHoldingWithReviewData = useCallback(
    (holding: Product, marketData: PortalHolding | undefined): Product => {
      if (!marketData) return holding;

      let enriched = holding;

      // Add Wine Advocate review if available
      if (marketData.waReviewer || marketData.waScore || marketData.wa) {
        enriched = {
          ...enriched,
          wa: marketData.wa ?? marketData.waScore,
          wineAdvocateReview: {
            reviewer: marketData.waReviewer,
            scoreDate: marketData.waScoreDate,
            score: marketData.waScore,
            drinkYearMin: marketData.waDrinkYearMin,
            drinkYearMax: marketData.waDrinkYearMax,
            tastingNote: marketData.waTastingNote,
            articleUrl: marketData.waArticleUrl,
          },
        };
      }

      // Add Vinous review if available
      if (marketData.vReviewer || marketData.vScoreMedian || marketData.vinous) {
        enriched = {
          ...enriched,
          vinous: marketData.vinous ?? marketData.vScoreMedian?.toString() ?? null,
          vinousReview: {
            reviewer: marketData.vReviewer,
            reviewDate: marketData.vReviewDate,
            score: marketData.vScoreMedian,
            drinkFrom: marketData.vDrinkFrom,
            drinkTo: marketData.vDrinkTo,
            tastingNote: marketData.vTastingNote,
            externalLink: marketData.vExternalLink,
          },
        };
      }

      // Add quality metrics if available
      enriched = {
        ...enriched,
        grade: marketData.grade ?? enriched.grade,
        fairValueScore: marketData.fairValueScore ?? enriched.fairValueScore,
        liquidityScore: marketData.liquidityScore ?? enriched.liquidityScore,
        performanceScore: marketData.performanceScore ?? enriched.performanceScore,
        vintageScore: marketData.vintageScore ?? enriched.vintageScore,
        marketPrice: marketData.marketPrice ?? enriched.marketPrice,
        combinedScore: marketData.normalizedCriticScore ?? enriched.combinedScore,
      };

      return enriched;
    },
    [],
  );

  // Memoize enriched current holdings separately from tabs
  // Show data immediately, enrich with review data when available (non-blocking)
  const enrichedCurrentHoldings = useMemo(() => {
    if (!currentHoldings.length) return [];

    return currentHoldings.map((x) => {
      let modified: Product = x;

      // Convert bid/offer prices
      if (x?.highestBid?.price) {
        modified = {
          ...modified,
          highestBid: {
            ...x.highestBid,
            priceInTargetCurrency: gbpToTargetCurrency.convert(x.highestBid?.price ?? 0),
            priceGbp: x.highestBid.price,
          },
        };
      }

      if (x?.lowestOffer?.price) {
        modified = {
          ...modified,
          lowestOffer: {
            ...x.lowestOffer,
            priceInTargetCurrency: gbpToTargetCurrency.convert(x.lowestOffer?.price ?? 0),
            priceGbp: x.lowestOffer.price,
          },
        };
      }

      // Enrich with review data if available (non-blocking - data shows immediately)
      const holdingMarketData = marketInsights.getHoldingInsight(x.lwin18);
      if (holdingMarketData) {
        modified = enrichHoldingWithReviewData(modified, holdingMarketData);
      }

      return {
        ...modified,
        latestDealDate: x.latestDealDate ? new Date(x.latestDealDate) : DEFAULT_LATEST_DEAL_DATE,
        highestBidPrice:
          modified?.physicalQuantity && modified.physicalQuantity > 0
            ? modified?.highestBid?.priceInTargetCurrency ?? 0
            : null,
        lowestOfferPrice: modified?.lowestOffer?.priceInTargetCurrency ?? 0,
        [DATA_REFS.SANITIZED_WINE_NAME]: x[DATA_REFS.NAME].normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
      };
    });
  }, [currentHoldings, gbpToTargetCurrency, marketInsights, enrichHoldingWithReviewData]);

  const refreshCurrentStocksTotals = (tableData: Product[]) => {
    const totalHoldings = sumBy(tableData, 'totalValue');
    const totalPurchase = sumBy(tableData, 'totalCost');
    //  const totalMgmtFee = sumBy(tableData, 'totalMgmtFee');
    const totalProfitAndLoss = totalHoldings - totalPurchase;
    const totalPAndLPercent = totalPurchase > 0 ? (totalProfitAndLoss / totalPurchase) * 100 : 0;
    return [
      {
        title: t('portfolio:summary.texts.portfolioValuation'),
        value: totalHoldings,
        text: `${formatter.format(totalHoldings)}`,
      },
      {
        title: t('portfolio:titles.details_totals.total-purchase-price'),
        value: totalPurchase,
        text: `${formatter.format(totalPurchase)}`,
      },
      {
        title: t('portfolio:titles.details_totals.total-profit-loss'),
        value: totalProfitAndLoss,
        text: `${formatter.format(totalProfitAndLoss)}`,
        additionalText: ` (${totalPAndLPercent.toFixed(2)}%)`,
        color: totalProfitAndLoss > 0 ? '#68CA5D' : '#FF5C5C',
      },
    ];
  };

  const refreshSoldStocksTotals = (tableData: Product[]) => {
    const totalHoldings = sumBy(tableData, 'totalValue');
    const totalPurchase = sumBy(tableData, 'totalCost');
    const totalProfitAndLoss = totalHoldings - totalPurchase;
    const totalPAndLPercent = totalPurchase > 0 ? (totalProfitAndLoss / totalPurchase) * 100 : 0;
    return [
      {
        title: t('portfolio:titles.details_totals.total-sold-value'),
        value: totalHoldings,
        text: `${formatter.format(totalHoldings)}`,
      },
      {
        title: t('portfolio:titles.details_totals.total-purchase-price'),
        value: totalPurchase,
        text: `${formatter.format(totalPurchase)}`,
      },
      {
        title: t('portfolio:titles.details_totals.total-profit-loss'),
        value: totalProfitAndLoss,
        text: `${formatter.format(totalProfitAndLoss)}`,
        additionalText: ` (${totalPAndLPercent.toFixed(2)}%)`,
        color: totalProfitAndLoss > 0 ? '#68CA5D' : '#FF5C5C',
      },
    ];
  };

  // Memoize sold stocks datasource
  const enrichedSoldStocks = useMemo(() => {
    if (!soldStocks.length) return [];
    return soldStocks.map((x, i) => ({
      ...x,
      id: `sold-${i + 1}`, // Unique ID for table rows (preserve original lwin18 for wine images)
      [DATA_REFS.SANITIZED_WINE_NAME]: x[DATA_REFS.NAME].normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
    }));
  }, [soldStocks]);

  const tabs = useMemo(
    () => [
      {
        id: TabTypes.CURRENT,
        value: TabTypes.CURRENT,
        title: <span className="text-14">{capitalizeFirstLetter(t(`portfolio:${TabTypes.CURRENT}`))}</span>,
        content: () => (
          <StockTab
            selectedPortfolioId={selectedPortfolioId}
            sortIsAsc={location?.state?.isAsc}
            defaultSortKey={location?.state?.defaultSort ?? defaultSortKey}
            disableSort={true}
            searchQueryIn={searchQuery}
            refreshTotals={refreshCurrentStocksTotals}
            showCompactSwitch={true}
            scrollContained={true}
            stickyFirstColumn={true}
            toolbarExtra={<PortfolioViewToggle value={currentViewMode} onChange={setCurrentViewMode} />}
            columns={columns}
            sortByOptions={sortByOptions as SortByOption[]}
            defaultSortBy={currentDefaultSortBy}
            tabState={tabStates[selectedTab]}
            filterPanelContainerClassName="px-5"
            id={TabTypes.CURRENT}
            datasource={enrichedCurrentHoldings}
            onStateChange={onStateChange}
            loading={loadingCurrent && enrichedCurrentHoldings.length === 0}
            totalCount={totalHoldingsCount}
            error={currentError ? new Error(JSON.stringify(currentError)) : null}
          />
        ),
      },
      {
        id: TabTypes.SOLD,
        value: TabTypes.SOLD,
        title: <span className="text-14">{capitalizeFirstLetter(t(`portfolio:${TabTypes.SOLD}`))}</span>,
        content: () => (
          <>
            <TradeDistribution
              soldHoldings={enrichedSoldStocks}
              isLoading={selectedTab === TabTypes.SOLD && loadingSoldStocks}
            />
            <StockTab
              selectedPortfolioId={selectedPortfolioId}
              disableSort={true}
              refreshTotals={refreshSoldStocksTotals}
              columns={soldStockColumns}
              sortByOptions={soldSortByOptions}
              defaultSortBy={soldDefaultSortBy}
              disableTableClick={true}
              scrollContained={true}
              stickyFirstColumn={true}
              tabState={tabStates[selectedTab]}
              id={TabTypes.SOLD}
              filterOverrides={{
                [FilterTypes.STATUES]: {
                  source: [t('portfolio:filters.processing'), t('portfolio:filters.sold')],
                  filterFn: (data: Product[], ids: string[]) => {
                    let statusesFilterResult: Product[] = [];
                    uniqueItems(data.map((x) => x.status)).forEach((status) => {
                      if (ids.includes(toInternalId(status!))) {
                        statusesFilterResult = [
                          ...statusesFilterResult,
                          ...data.filter((x) => x.status?.toLowerCase() === status?.toLowerCase()),
                        ];
                      }
                    });
                    return statusesFilterResult;
                  },
                },
              }}
              filterPanelContainerClassName="px-5"
              datasource={enrichedSoldStocks}
              onStateChange={onStateChange}
              loading={selectedTab === TabTypes.SOLD && loadingSoldStocks}
              error={soldStockError ? new Error(JSON.stringify(soldStockError)) : null}
            />
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      enrichedCurrentHoldings,
      loadingCurrent,
      currentError,
      enrichedSoldStocks,
      loadingSoldStocks,
      soldStockError,
      selectedTab,
      totalHoldingsCount,
      currentViewMode,
      columns,
    ],
  );
  useEffect(() => {
    // reFetchCurrentHoldings();
    // reFetchSoldStock();
    // reFetchExternalStock();
    if (0) {
      fetchExternalHoldings();
    }
  }, [currency]);

  return (
    <div className="h-100 flex-1 bg-gray-50 dark:bg-slate-900 flex overflow-y-auto flex-col">
      <Tabs className="pb-5 px-4" items={tabs} onItemSelect={onTabSelect} value={selectedTab} />
      <VerifyTopUpAndContinueToPurchase
        config={{
          success: {
            onSuccess: () => {
              setTimeout(() => {
                queryClient.resetQueries({ queryKey: ['fetchCurrentHoldingVT2', { currency }] });
              }, HOLDING_TIME_OUT);
            },
          },
        }}
      />
    </div>
  );
};
